import useTextDebounce from '@utils/useTextDebounce';
import {
  startTransition,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTradingHub } from '../tradingHubContext';
import type { ViewMode } from '../utils/portfolioFilters';

type ResetFiltersOptions = {
  preserveSearch?: boolean;
  setViewModePortfolios?: boolean;
  statusFilter?: string;
  pageSize?: number;
};

export function usePortfolioFilters({
  setPage,
  setPageSize,
  setViewMode,
}: {
  setPage: (page: number | ((prev: number) => number)) => void;
  setPageSize: (size: number) => void;
  setViewMode: (mode: ViewMode) => void;
}) {
  const {
    portfolioStatusPreFilter,
    setPortfolioStatusPreFilter,
    pinnedPortfolioDeepLink,
  } = useTradingHub();

  const [searchTerm, setSearchTerm] = useState('');
  // Intentionally slower than TOOLBAR_SEARCH_DEBOUNCE_MS — full table refetch vs. suggestion dropdown.
  const debouncedSearch = useTextDebounce({ value: searchTerm, delay: 300 });
  const [statusFilter, setStatusFilter] = useState('ANY');
  const [portfolioTypeFilter, setPortfolioTypeFilter] = useState('ANY');
  const [modelFilter, setModelFilter] = useState<string[]>([]);
  const [selectedModelNames, setSelectedModelNames] = useState<
    Record<string, string>
  >({});

  const skipSearchPageResetRef = useRef(false);
  const debouncedSearchRef = useRef(debouncedSearch);
  debouncedSearchRef.current = debouncedSearch;
  const lastPinnedReportIdRef = useRef<string | null>(null);

  const resetFilters = useCallback(
    (options: ResetFiltersOptions = {}) => {
      startTransition(() => {
        if (!options.preserveSearch) setSearchTerm('');
        setStatusFilter(options.statusFilter ?? 'ANY');
        setPortfolioTypeFilter('ANY');
        setModelFilter([]);
        setSelectedModelNames({});
        if (options.setViewModePortfolios) setViewMode('portfolios');
        if (options.pageSize !== undefined) setPageSize(options.pageSize);
        setPage(1);
      });
    },
    [setPage, setPageSize, setViewMode],
  );

  useEffect(() => {
    if (skipSearchPageResetRef.current) {
      skipSearchPageResetRef.current = false;
      return;
    }
    startTransition(() => setPage(1));
  }, [debouncedSearch, setPage]);

  // Deep-link pin takes precedence over dashboard status pre-filter when both
  // arrive in the same tick: pinning targets one portfolio and should clear
  // competing list filters. Status pre-filter applies only when no pin is active.
  useEffect(() => {
    if (pinnedPortfolioDeepLink) {
      const pinId = pinnedPortfolioDeepLink.portfolioReportId;
      if (lastPinnedReportIdRef.current !== pinId) {
        lastPinnedReportIdRef.current = pinId;
        if (portfolioStatusPreFilter) {
          setPortfolioStatusPreFilter(null);
        }
        if (debouncedSearchRef.current !== '') {
          skipSearchPageResetRef.current = true;
        }
        resetFilters({ setViewModePortfolios: true });
      }
      return;
    }

    lastPinnedReportIdRef.current = null;

    if (!portfolioStatusPreFilter) return;
    const filter = portfolioStatusPreFilter;
    setPortfolioStatusPreFilter(null);
    resetFilters({ statusFilter: filter, pageSize: 100 });
  }, [
    portfolioStatusPreFilter,
    setPortfolioStatusPreFilter,
    pinnedPortfolioDeepLink,
    resetFilters,
  ]);

  const handleClearFilters = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  const handleModelChange = useCallback(
    (ids: string[], options: { id: string; name: string }[] = []) => {
      startTransition(() => {
        setModelFilter(ids);
        setSelectedModelNames((prev) => {
          const next = { ...prev };
          for (const option of options) {
            next[option.id] = option.name;
          }
          for (const id of Object.keys(next)) {
            if (!ids.includes(id)) delete next[id];
          }
          return next;
        });
        setPage(1);
      });
    },
    [setPage],
  );

  const handleFilterChange = useCallback(
    (setter: (v: string) => void) => (value: string) => {
      startTransition(() => {
        setter(value);
        setPage(1);
      });
    },
    [setPage],
  );

  const handlePageSizeChange = useCallback(
    (size: number) => {
      startTransition(() => {
        setPageSize(size);
        setPage(1);
      });
    },
    [setPage, setPageSize],
  );

  const hasActiveFilters =
    statusFilter !== 'ANY' ||
    portfolioTypeFilter !== 'ANY' ||
    modelFilter.length > 0 ||
    Boolean(searchTerm);

  return {
    searchTerm,
    setSearchTerm,
    debouncedSearch,
    statusFilter,
    setStatusFilter,
    portfolioTypeFilter,
    setPortfolioTypeFilter,
    modelFilter,
    selectedModelNames,
    hasActiveFilters,
    handleClearFilters,
    handleModelChange,
    handleFilterChange,
    handlePageSizeChange,
  };
}
